import { useState } from "react";
import { Modal, Input, Upload, Button, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { putTranscript } from "../../../services/api";

/**
 * Demo/testing-only transcript upload.
 *
 * Lets a reviewer paste or upload a plain-text transcript for a session instead
 * of recording a live conversation. On submit it PUTs the transcript to the
 * backend, then hands the text back so the session view can show it and enable
 * Generate AI Notes. Gated to the demo build by the caller.
 */
export default function TranscriptUploadModal({
  open,
  onClose,
  patientId,
  sessionId,
  onUploaded,
}) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const readFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => setText(String(e.target?.result || ""));
    reader.onerror = () => message.error("Could not read that file.");
    reader.readAsText(file);
    return false; // prevent antd's default upload
  };

  const submit = async () => {
    const content = text.trim();
    if (!content) {
      message.warning("Paste a transcript or choose a .txt file first.");
      return;
    }
    setSaving(true);
    try {
      await putTranscript(patientId, sessionId, content);
      onUploaded?.(content);
      message.success("Transcript uploaded. You can now Generate AI Notes.");
      setText("");
      onClose?.();
    } catch (err) {
      message.error(
        err?.response?.data?.detail || err?.message || "Could not upload the transcript."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Upload transcript (demo)"
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="Upload"
      confirmLoading={saving}
      width={640}
    >
      <p className="mb-3 text-sm text-gray-500">
        Paste a conversation transcript, or load a .txt file. This is for testing
        the scribe without a live recording.
      </p>
      <Upload beforeUpload={readFile} maxCount={1} accept=".txt" showUploadList={false}>
        <Button icon={<UploadOutlined />} size="small" className="mb-3">
          Choose .txt file
        </Button>
      </Upload>
      <Input.TextArea
        rows={12}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Provider: What brings you in today?&#10;Patient: ..."
      />
    </Modal>
  );
}
